// HeaderHome.jsx
import React from "react";
import {
  Box,
  Container,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { keyframes } from "@emotion/react";
import HeroSlider from "./HeroSlider";
import { trustItems } from "./HeroData";

// ---- انیمیشن‌ها ----
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const drift = keyframes`
  0%   { transform: translate(0, 0); }
  50%  { transform: translate(24px, -18px); }
  100% { transform: translate(0, 0); }
`;

const drawUnderline = keyframes`
  from { transform: scaleX(0); }
  to   { transform: scaleX(1); }
`;

// هلپر برای ورودی مرحله‌به‌مرحله — هر بخش با تأخیر خودش میاد
const enter = (delay) => ({
  opacity: 0,
  animation: `${fadeUp} .7s cubic-bezier(.2,.7,.2,1) forwards`,
  animationDelay: `${delay}s`,
});

export default function Header({ sliderData, isLoading, isFetching, isError }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        direction: "rtl",
        bgcolor: theme.palette.background.default,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* بلوب‌های محو پس‌زمینه — خیلی کم‌رنگ، فقط عمق می‌دن */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          top: -120,
          insetInlineEnd: -80,
          width: 360,
          height: 360,
          borderRadius: "50%",
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
          filter: "blur(70px)",
          animation: `${drift} 12s ease-in-out infinite`,
          pointerEvents: "none",
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          bottom: -100,
          insetInlineStart: -60,
          width: 300,
          height: 300,
          borderRadius: "50%",
          bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.08),
          filter: "blur(70px)",
          animation: `${drift} 14s ease-in-out infinite reverse`,
          pointerEvents: "none",
        }}
      />

      <Container
        maxWidth="lg"
        sx={{ py: { xs: 6, md: "64px" }, position: "relative" }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems="center"
          spacing={{ xs: 5, md: 7.5 }}
        >
          {/* متن سمت راست */}
          <Box sx={{ flex: 1, width: "100%" }}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.08),
                color: "#C2410C",
                fontFamily: "Dana",
                fontSize: 13.5,
                fontWeight: 700,
                px: 2,
                py: 1,
                borderRadius: 999,
                mb: 3,
                ...enter(0),
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  bgcolor: "secondary.main",
                }}
              />
              همکار قابل‌اعتماد خانه شما
            </Box>

            <Typography
              component="h1"
              sx={{
                fontFamily: "YekanPlus",
                fontWeight: 800,
                fontSize: { xs: 32, sm: 40, md: 52 },
                lineHeight: 1.22,
                color: "primary.main",
                mb: 2.75,
                ...enter(0.12),
              }}
            >
              خدمات آنلاین و تعمیرات
              <br />
              <Box
                component="span"
                sx={{ position: "relative", display: "inline-block" }}
              >
                <Box component="span" sx={{ color: "secondary.main" }}>
                  در منزل شما
                </Box>
                {/* خط موج‌دار زیر عبارت — بعد از اومدن تیتر کشیده میشه */}
                <Box
                  component="svg"
                  viewBox="0 0 200 14"
                  preserveAspectRatio="none"
                  aria-hidden
                  sx={{
                    position: "absolute",
                    insetInlineStart: 0,
                    bottom: -8,
                    width: "100%",
                    height: 14,
                    overflow: "visible",
                    transformOrigin: "right center",
                    transform: "scaleX(0)",
                    animation: `${drawUnderline} .9s ease .75s forwards`,
                  }}
                >
                  <Box
                    component="path"
                    d="M2 9 Q 25 2, 50 9 T 100 9 T 150 9 T 198 9"
                    fill="none"
                    stroke="#FF6B35"
                    strokeWidth="4"
                    strokeLinecap="round"
                  />
                </Box>
              </Box>
            </Typography>

            <Typography
              sx={{
                fontFamily: "Dana",
                fontSize: 17,
                lineHeight: 1.9,
                color: "text.secondary",
                maxWidth: 460,
                mb: 5,
                ...enter(0.24),
              }}
            >
              با راما به‌راحتی می‌توانید انواع خدمات و تعمیرات مورد نیاز منزل
              خود را پیدا کنید و با بهترین متخصصان شهر خود در ارتباط باشید.
            </Typography>

            <Stack
              direction="row"
              spacing={3.5}
              flexWrap="wrap"
              sx={enter(0.36)}
            >
              {trustItems.map((item) => (
                <Stack
                  key={item.id}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    cursor: "default",
                    transition: "transform .25s ease",
                    "&:hover": { transform: "translateY(-3px)" },
                    "&:hover .trust-icon": {
                      bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, 0.1),
                    },
                  }}
                >
                  <Box
                    className="trust-icon"
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "primary.main",
                      transition: "background-color .25s ease",
                    }}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      {item.circle && <circle cx="12" cy="12" r="9" />}
                      {item.rect && (
                        <rect x="2" y="6" width="20" height="14" rx="2" />
                      )}
                      <path d={item.path} />
                    </svg>
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: "Dana",
                      fontSize: 13.5,
                      fontWeight: 600,
                      color: "text.primary",
                    }}
                  >
                    {item.label}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>

          {/* اسلایدر سمت چپ — دیتاش رو خودش از useGetSliderPageQuery می‌گیره */}
          <Box sx={{ flex: 1, width: "100%", ...enter(0.18) }}>
            <HeroSlider
              sliderData={sliderData}
              isLoading={isLoading}
              isFetching={isFetching}
              isError={isError}
            />
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}
